/**
 * OpenTelemetry Tracing Configuration
 *
 * This module is completely isolated from application code.
 * Enable/disable via REACT_APP_ENABLE_TRACING environment variable.
 */

import { WebTracerProvider } from "@opentelemetry/sdk-trace-web";
import { BatchSpanProcessor } from "@opentelemetry/sdk-trace-base";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { getWebAutoInstrumentations } from "@opentelemetry/auto-instrumentations-web";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { resourceFromAttributes } from "@opentelemetry/resources";
import { SemanticResourceAttributes } from "@opentelemetry/semantic-conventions";
import {
  CompositePropagator,
  W3CTraceContextPropagator,
} from "@opentelemetry/core";
import { B3Propagator, B3InjectEncoding } from "@opentelemetry/propagator-b3";
import { ZoneContextManager } from "@opentelemetry/context-zone";

const TRACING_ENABLED = true;
const SERVICE_NAME = "oms-ui";
// Traces are sent to the same domain as the app, then proxied by Nginx
const OTEL_COLLECTOR_URL = window.location.origin + "/v1/traces";

export function initializeTracing() {
  if (!TRACING_ENABLED) {
    return;
  }

  const exporter = new OTLPTraceExporter({
    url: OTEL_COLLECTOR_URL,
  });

  const provider = new WebTracerProvider({
    resource: resourceFromAttributes({
      [SemanticResourceAttributes.SERVICE_NAME]: SERVICE_NAME,
    }),
    spanProcessors: [new BatchSpanProcessor(exporter)],
  });

  provider.register({
    contextManager: new ZoneContextManager(),
    propagator: new CompositePropagator({
      propagators: [
        new W3CTraceContextPropagator(),
        new B3Propagator({ injectEncoding: B3InjectEncoding.MULTI_HEADER }),
        new B3Propagator({ injectEncoding: B3InjectEncoding.SINGLE_HEADER }),
      ],
    }),
  });

  registerInstrumentations({
    instrumentations: [
      getWebAutoInstrumentations({
        "@opentelemetry/instrumentation-xml-http-request": {
          propagateTraceHeaderCorsUrls: [
            /^\//, // MATCHES RELATIVE PATHS (e.g., /joms/api/...)
            /https?:\/\/.*\.jswone\.in/,
            /https?:\/\/.*\.jswonemsme\.com/,
            /http:\/\/localhost:\d+/,
            /http:\/\/127.0.0.1:\d+/,
          ],
        },
        "@opentelemetry/instrumentation-fetch": {
          propagateTraceHeaderCorsUrls: [
            /^\//, // MATCHES RELATIVE PATHS
            /https?:\/\/.*\.jswone\.in/,
            /https?:\/\/.*\.jswonemsme\.com/,
            /http:\/\/localhost:\d+/,
            /http:\/\/127.0.0.1:\d+/,
          ],
        },
        "@opentelemetry/instrumentation-user-interaction": {
          enabled: true,
          eventNames: [
            "click",
            "load",
            "loadeddata",
            "loadedmetadata",
            "loadstart",
            "error",
            "submit",
          ],
        },
      }),
    ],
  });
}

// Export a no-op tracer for manual instrumentation (optional)
export { trace } from "@opentelemetry/api";
